/** Transport-independent, declarative tool and interaction presentation. */

import type {
  ToolArtifact,
  ToolResultPayload,
} from '@web/adapter/toThreadMessages'
import type { AppToolPresentation } from '@web/api/types'
import type { BridgeState } from '@web/state/bridge'

export function normalizedOrigin(origin: unknown): string {
  if (typeof origin === 'string') return origin
  return origin && typeof origin === 'object' && 'kind' in origin
    ? String(origin.kind)
    : 'user'
}

/** Only explicit catalog aliases can select an authored presentation. */
export function declaredPresentation(
  name: string,
  bridge?: BridgeState,
): AppToolPresentation | undefined {
  const tools = bridge?.record?.config.app_configuration?.rendering?.tools ?? []
  return tools.find((tool) => {
    const configuration = bridge?.record?.config.app_configuration
    const backend = configuration?.backend.mode
    const server =
      tool.source === 'app'
        ? backend === 'per_session' ||
          backend === 'shared' ||
          configuration?.demo
          ? 'app-backend'
          : 'greeting-studio'
        : 'greeting-browser'
    const aliases = [
      tool.name,
      ...[server, server.replaceAll('-', '_')].flatMap((id) =>
        [tool.name, tool.name.replaceAll('.', '_')].map(
          (action) => `mcp__${id}__${action}`,
        ),
      ),
    ]
    return aliases.includes(name)
  })
}

export function appToolReference(
  frame?: Record<string, any>,
): ToolArtifact['appTool'] {
  if (
    frame?.verified !== true ||
    ![
      'action',
      'binding_id',
      'invocation_id',
      'artifact_id',
      'package_digest',
      'resource_uri',
    ].every((key) => typeof frame[key] === 'string' && frame[key])
  )
    return undefined
  return {
    name: frame.action,
    bindingId: frame.binding_id,
    invocationId: frame.invocation_id,
    artifactId: frame.artifact_id,
    packageDigest: frame.package_digest,
    resourceUri: frame.resource_uri,
    resourceDigest: frame.resource_digest,
    verified: true,
  }
}

/** Text blocks stay readable; structured values remain available separately. */
export function outputText(value: unknown): string {
  if (typeof value === 'string') return value
  if (Array.isArray(value))
    return value.map(outputText).filter(Boolean).join('\n')
  if (value && typeof value === 'object') {
    const block = value as Record<string, unknown>
    if (block.type === 'text' && typeof block.text === 'string')
      return block.text
    if (block.type === 'content') return outputText(block.content)
    if (block.type === 'diff')
      return typeof block.newText === 'string' ? block.newText : ''
  }
  return ''
}

export function verifiedResult(frame: Record<string, any>): ToolResultPayload {
  return {
    text: outputText(frame.result_content),
    raw: frame.result_content,
    structured: frame.structured_result,
    isError: frame.is_error === true,
  }
}
